'use strict';

import Swiper from 'swiper';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';

const SELECTOR = '.js-gallery';

export function init() {
    const elements = document.querySelectorAll(SELECTOR);

    if (!elements.length) {
        return;
    }

    elements.forEach(element => {
        const slidesContainerSelector = element.querySelector('.image-gallery__content');
        const slideDetailsSelector = element.dataset.gallerySlideDetailsSelector || '.swiper-slide-active .gallery-slide__content';

        const fixedBodyClass = element.dataset.galleryFixedBodyClass || 'image-gallery-fixed-body';
        const fullscreenClass = element.dataset.galleryFullscreenClass || 'image-gallery--fullscreen';

        const detailsContainer = element.querySelector(
            element.dataset.galleryDetailsContainerSelector || '.image-gallery__details'
        );

        const positionContainer = element.querySelector(
            element.dataset.galleryPositionContainerSelector || '.image-gallery__position'
        );

        const prevButton = element.querySelector(
            element.dataset.galleryPrevButtonSelector || '.image-gallery__button--previous'
        );

        const nextButton = element.querySelector(
            element.dataset.galleryNextButtonSelector || '.image-gallery__button--next'
        );

        const fullscreenButton = element.querySelector(
            element.dataset.galleryFullscreenButtonSelector || '.image-gallery__button--fullscreen'
        );

        const images = element.querySelectorAll(
            element.dataset.galleryImageSelector || '.gallery-slide__image'
        );

        const isSingleSlide = element.querySelectorAll('.image-gallery__slide').length === 1;

        if (isSingleSlide) {
            prevButton.classList.add('disabled');
            nextButton.classList.add('disabled');
        }

        let swiper = new Swiper(slidesContainerSelector, {
            init: false,
            modules: [
                Navigation,
                Pagination,
                Keyboard
            ],
            speed: 400,
            slidesPerView: 'auto',
            direction: 'horizontal',
            watchOverflow: true,
            simulateTouch: true,
            centeredSlides: false,
            autoplay: false,
            observer: true,
            observeParents: true,
            preventInteractionOnTransition: true,
            navigation: {
                prevEl: prevButton,
                nextEl: nextButton,
            },
            pagination: {
                el: positionContainer,
                type: 'fraction',
            },
        });

        swiper.on('init', updateMeta);
        swiper.on('slideChangeTransitionEnd', updateMeta);
        swiper.on('slideChange', updateNavigationButtons);

        swiper.init();

        if (fullscreenButton) {
            fullscreenButton.addEventListener('click', () => {
                if (isGalleryFullscreen()) {
                    endFullscreen();
                } else {
                    startFullscreen();
                }
            }, false);
        }

        document.addEventListener('keyup', event => {
            if (event.defaultPrevented) {
                return;
            }

            if (isGalleryFullscreen() === false) {
                return;
            }

            switch (event.key || event.code) {
                case 'Escape':
                case 'Esc':
                    endFullscreen();
                    break;
                case 'ArrowLeft':
                case 'Left':
                    swiper.slidePrev();
                    break;
                case 'ArrowRight':
                case 'Right':
                    swiper.slideNext();
                    break;
                default:
                    return;
            }
        });

        function updateMeta() {
            const slideDetailContainer = element.querySelector(slideDetailsSelector);

            if (!slideDetailContainer || !detailsContainer) {
                return;
            }

            detailsContainer.innerHTML = slideDetailContainer.innerHTML;
        }

        function updateNavigationButtons() {
            if (swiper.isBeginning) {
                prevButton.classList.add('disabled');
            } else {
                prevButton.classList.remove('disabled');
            }

            if (swiper.isEnd) {
                nextButton.classList.add('disabled');
            } else {
                nextButton.classList.remove('disabled');
            }
        }

        function isGalleryFullscreen() {
            return element.classList.contains(fullscreenClass);
        }

        let documentScrollTop = 0;

        function trapFocus(event) {
            const focusableElements = Array.from(element.querySelectorAll('button, a, input, [tabindex="0"]')).filter(el => !el.classList.contains('disabled'));
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            if (event.key === 'Tab') {
                if (event.shiftKey) {  // If Shift + Tab is pressed (backward tabbing)
                    if (document.activeElement === firstFocusable) {
                        lastFocusable.focus();  // Move focus to the last focusable element
                        event.preventDefault();
                    }
                } else {  // Regular Tab key (forward tabbing)
                    if (document.activeElement === lastFocusable) {
                        firstFocusable.focus();  // Move focus to the first focusable element
                        event.preventDefault();
                    }
                }
            }
        }

        function startFullscreen() {
            switchImages();
            element.classList.add(fullscreenClass);
            documentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            document.body.classList.add(fixedBodyClass);
            document.querySelector('.image-gallery__fullscreen-close').focus();
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', trapFocus);
        }

        function endFullscreen() {
            switchImages();
            element.classList.remove(fullscreenClass);
            document.body.classList.remove(fixedBodyClass);
            document.documentElement.scrollTop = document.body.scrollTop = documentScrollTop;
            document.body.style.overflow = '';
            document.removeEventListener('keydown', trapFocus);
        }

        function switchImages() {
            images.forEach(image => {
                const { fullscreenSrc } = image.dataset;

                if (!fullscreenSrc) {
                    return;
                }

                image.dataset.fullscreenSrc = image.src;
                image.src = fullscreenSrc;
            });
        }
    });
}